import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { formatDistanceToNow } from 'date-fns';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsQuery,
  useUnreadNotificationCount
} from '@/Modules/HelpdeskModule/shared/hooks/useHelpdeskQueries';
import { HelpdeskNotification, NotificationType } from '@/Modules/HelpdeskModule/shared/types';
import { useLocalization } from '@/shared/hooks/useLocalization';

const notificationIcon = (type: NotificationType): React.ReactElement => {
  switch (type) {
    case NotificationType.TICKET_CREATED:
      return <ConfirmationNumberOutlinedIcon fontSize="small" color="primary" />;
    case NotificationType.ASSIGNED:
      return <PersonAddOutlinedIcon fontSize="small" color="info" />;
    case NotificationType.STATUS_CHANGED:
      return <UpdateOutlinedIcon fontSize="small" color="warning" />;
    case NotificationType.COMMENT_ADDED:
      return <ChatBubbleOutlineIcon fontSize="small" color="secondary" />;
    case NotificationType.RESOLVED:
      return <CheckCircleOutlineIcon fontSize="small" color="success" />;
    default:
      return <NotificationsNoneIcon fontSize="small" />;
  }
};

export const NotificationBell: React.FC = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { data: notifications = [] } = useNotificationsQuery();
  const unreadCount = useUnreadNotificationCount();
  const markRead = useMarkNotificationReadMutation();
  const markAllRead = useMarkAllNotificationsReadMutation();

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleItemClick = (notification: HelpdeskNotification): void => {
    if (!notification.read) {
      markRead.mutate(notification.id);
    }
    navigate(`/helpdesk/detail/${notification.ticketId}`);
    handleClose();
  };

  const handleMarkAllRead = (): void => {
    markAllRead.mutate();
    handleClose();
  };

  return (
    <>
      <IconButton aria-label="notifications" onClick={handleOpen}>
        <Badge badgeContent={unreadCount} color="error" max={99} invisible={unreadCount === 0}>
          {unreadCount > 0 ? (
            <NotificationsActiveIcon color="primary" />
          ) : (
            <NotificationsNoneIcon color="primary" />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 420 } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant="h6">{t('SHARED.NOTIFICATION_BELL.TITLE')}</Typography>
          <Button size="small" disabled={unreadCount === 0} onClick={handleMarkAllRead}>
            {t('SHARED.NOTIFICATION_BELL.MARK_ALL_READ')}
          </Button>
        </Stack>

        <Divider />

        {notifications.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 4, gap: 1 }}
          >
            <NotificationsOffIcon color="disabled" fontSize="large" />
            <Typography variant="body2" color="text.secondary">
              {t('SHARED.NOTIFICATION_BELL.NO_NOTIFICATIONS')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflowY: 'auto', maxHeight: 340 }}>
            {notifications.map((notification) => (
              <React.Fragment key={notification.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleItemClick(notification)}
                    sx={{ bgcolor: notification.read ? 'transparent' : 'action.hover' }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {notificationIcon(notification.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={notification.message}
                      secondary={`${notification.ticketTitle} · ${formatDistanceToNow(
                        new Date(notification.createdAt),
                        { addSuffix: true }
                      )}`}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: notification.read ? 400 : 600,
                        noWrap: false
                      }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
